import React, { useState } from "react";
import { Check, Copy } from "lucide-react";
import { OrchestrationSlot } from "../../../../types/orchestration";
import {
  formatDuration,
  formatElapsed,
  formatSlotLabel,
} from "../../../../utils/orchestrationLayout";
import { copyToClipboard } from "../../../../utils/clipboard";
import {
  SLOT_BIG_CLASS,
  SLOT_PILL_CLASS,
  SLOT_STATUS_LABEL,
} from "./orchestrationStyles";
import StepStatusIcon from "./StepStatusIcon";

interface SlotRailProps {
  slots: OrchestrationSlot[];
  selectedKey: string;
  onSelect: (slotUtc: string) => void;
  nowMs: number;
}

const ExecCopy: React.FC<{ exec: string }> = ({ exec }) => {
  const [copied, setCopied] = useState(false);
  return (
    <div className="mt-2 flex items-center gap-1.5 font-mono text-[10.5px] text-gray-400 dark:text-gray-500">
      <span className="truncate" title={exec}>
        {exec}
      </span>
      <button
        type="button"
        aria-label="Copy execution name"
        onClick={(e) => {
          e.stopPropagation();
          copyToClipboard(exec)
            .then(() => {
              setCopied(true);
              window.setTimeout(() => setCopied(false), 1400);
            })
            .catch(() => undefined);
        }}
        title={copied ? "Copied" : "Copy execution name"}
        className={`flex-none rounded border p-1 transition-colors ${
          copied
            ? "border-green-500 text-green-600 dark:text-green-400"
            : "border-gray-300 text-gray-500 hover:border-rp-amber hover:text-gray-900 dark:border-rp-separator dark:text-gray-400 dark:hover:text-white"
        }`}
      >
        {copied ? <Check size={12} /> : <Copy size={12} />}
      </button>
    </div>
  );
};

const SlotRail: React.FC<SlotRailProps> = ({
  slots,
  selectedKey,
  onSelect,
  nowMs,
}) => {
  return (
    <aside className="flex w-[268px] flex-none flex-col gap-2.5 overflow-y-auto border-r border-gray-200 bg-gray-50 p-3 dark:border-rp-separator dark:bg-rp-surface-sunken">
      <div className="flex items-center justify-between px-1.5 pb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
        <span>Slots</span>
        <span>{slots.length} runs</span>
      </div>

      {slots.map((slot, idx) => {
        const ps = slot.phaseState;
        const active = slot.slotUtc === selectedKey;
        const isLatest = idx === 0;
        const big =
          ps.status === "in_progress"
            ? formatElapsed(ps.startedAtUtc, nowMs)
            : formatDuration(ps.durationMs);
        const failedStep = ps.steps.find((s) => s.status === "failed");

        return (
          <div
            key={slot.slotUtc}
            role="button"
            tabIndex={0}
            data-testid={`slot-rail-item-${slot.slotUtc}`}
            onClick={() => onSelect(slot.slotUtc)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onSelect(slot.slotUtc);
              }
            }}
            className={`cursor-pointer rounded-xl border border-l-[3px] p-3 text-left transition-all hover:-translate-y-px ${
              active
                ? "border-rp-amber/50 border-l-rp-amber bg-rp-amber/5 dark:bg-rp-surface-raised"
                : "border-gray-200 border-l-transparent bg-white hover:border-gray-300 dark:border-rp-separator dark:bg-rp-surface dark:hover:border-gray-600"
            }`}
          >
            <div className="flex items-center gap-2">
              {ps.status === "in_progress" && (
                <span className="h-2 w-2 animate-pulse rounded-full bg-rp-amber" />
              )}
              <span className="text-[13px] font-bold text-gray-900 dark:text-white">
                {formatSlotLabel(slot.slotUtc)}
              </span>
              {isLatest && (
                <span className="rounded border border-rp-amber/40 bg-rp-amber/10 px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-rp-amber">
                  Latest
                </span>
              )}
            </div>

            <div className="mt-2 flex items-center gap-2">
              <span
                className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${SLOT_PILL_CLASS[ps.status]}`}
              >
                {SLOT_STATUS_LABEL[ps.status]}
              </span>
              <span
                className={`font-mono text-[12px] tabular-nums ${SLOT_BIG_CLASS[ps.status]}`}
              >
                {big}
              </span>
            </div>

            {failedStep && (
              <div className="mt-2 flex items-center gap-1.5 text-[11px] font-semibold text-red-600 dark:text-red-400">
                <span aria-hidden>✕</span>
                {failedStep.name}
              </div>
            )}

            <ExecCopy exec={ps.orchestratorExecutionName} />

            <div className="mt-2.5 flex flex-wrap items-center gap-[2px]">
              {ps.steps.map((s) => (
                <span
                  key={s.id}
                  title={`${s.name}: ${s.status}`}
                  className="inline-flex"
                >
                  <StepStatusIcon status={s.status} size={12} />
                </span>
              ))}
            </div>
          </div>
        );
      })}
    </aside>
  );
};

export default SlotRail;
